export const FINANCE_MONTHLY_PILOT_USER_ID = 'eadeadd1-04cd-47fd-8ba4-26da974003c1'

export interface FinanceExpenseCategory {
  code: string
  label: string
  group_code: string
  group_label: string
  group_sort_order: number
  category_sort_order: number
  is_active: boolean
}

export interface FinanceExpenseField {
  code: string
  label: string
  isActive: boolean
}

export interface FinanceExpenseGroup {
  code: string
  label: string
  sortOrder: number
  fields: FinanceExpenseField[]
}

export type FinanceMonthlyFactValues = Record<string, number>

export interface FinanceMonthlyFactRow {
  studio_internal_id: number
  month_start: string
  values: FinanceMonthlyFactValues
  version: number
  updated_at: string
}

export function buildFinanceExpenseGroups(
  categories: FinanceExpenseCategory[],
  includedArchivedCodes: ReadonlySet<string> = new Set()
): FinanceExpenseGroup[] {
  const groups = new Map<string, FinanceExpenseGroup>()

  for (const category of [...categories].sort((left, right) => (
    left.group_sort_order - right.group_sort_order
      || left.category_sort_order - right.category_sort_order
      || left.code.localeCompare(right.code)
  ))) {
    if (!category.is_active && !includedArchivedCodes.has(category.code)) continue
    const group = groups.get(category.group_code) ?? {
      code: category.group_code,
      label: category.group_label,
      sortOrder: category.group_sort_order,
      fields: [],
    }
    group.fields.push({ code: category.code, label: category.label, isActive: category.is_active })
    groups.set(category.group_code, group)
  }

  return [...groups.values()]
}

export function financeGroupTotal(group: FinanceExpenseGroup, values: FinanceMonthlyFactValues): number {
  return group.fields.reduce((sum, field) => sum + (values[field.code] ?? 0), 0)
}

export function sanitizeFinanceValues(
  values: Record<string, number | null | undefined>,
  allowedCodes: ReadonlySet<string>
): FinanceMonthlyFactValues {
  return Object.fromEntries(
    Object.entries(values).filter((entry): entry is [string, number] => {
      const [code, value] = entry
      return allowedCodes.has(code) && typeof value === 'number' && Number.isFinite(value) && value >= 0
    })
  )
}
